import React from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, Chip, Collapse, Stack, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { CHECKLIST_STATUS_STYLES } from '../../constants';
import ApprovalActionButton from '../../reportCards/ApprovalActionButton';
import ChecklistStatusDot from '../../reportCards/ChecklistStatusDot';
import ManualChip from '../../reportCards/ManualChip';
import { getDealToggleLabel } from '../../shared/format';

import ValiditySection from './ValiditySection';
import { BUNDLED_EXPERIENCE_COLUMN_WIDTHS, BUNDLED_EXPERIENCE_NAME_COLUMN_MIN_WIDTH } from './constants';
import type { BundledExperienceRowView } from './types';

interface Props {
  approved: boolean;
  dealsExpanded: boolean;
  manuallyApproved: boolean;
  row: BundledExperienceRowView;
  onApprovalChange: (rowId: string) => void;
  onToggleDetails: (rowId: string) => void;
  onUnapprovalChange: (rowId: string) => void;
}

export default function BundledExperienceRow({
  approved,
  dealsExpanded,
  manuallyApproved,
  row,
  onApprovalChange,
  onToggleDetails,
  onUnapprovalChange,
}: Props) {
  const dealCount = row.linkedDealOptions.length;

  return (
    <>
      <TableRow
        hover
        sx={[
          approved ? CHECKLIST_STATUS_STYLES.approvedRow : undefined,
          dealCount > 0
            ? {
                cursor: 'pointer',
                '&:hover': { bgcolor: (theme) => alpha(theme.palette.text.primary, 0.04) },
              }
            : undefined,
        ]}
        onClick={dealCount > 0 ? () => onToggleDetails(row.id) : undefined}
      >
        <TableCell sx={{ minWidth: BUNDLED_EXPERIENCE_NAME_COLUMN_MIN_WIDTH }}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Stack sx={{ minWidth: 0, flex: 1 }}>
              <Tooltip title={row.bundledExperience}>
                <Typography
                  variant="body2"
                  fontWeight={500}
                  sx={{
                    cursor: 'inherit',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {row.bundledExperience}
                </Typography>
              </Tooltip>
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.25 }}>
                {dealCount > 0 && (
                  <Typography variant="caption" color="primary.main" fontWeight={600}>
                    {getDealToggleLabel({ dealCount, dealsExpanded })}
                  </Typography>
                )}
                {manuallyApproved && <ManualChip />}
              </Stack>
            </Stack>
            {dealCount > 0 &&
              (dealsExpanded ? (
                <KeyboardArrowUpIcon fontSize="small" color="action" sx={{ flexShrink: 0 }} />
              ) : (
                <KeyboardArrowDownIcon fontSize="small" color="action" sx={{ flexShrink: 0 }} />
              ))}
          </Stack>
        </TableCell>
        <TableCell sx={{ width: BUNDLED_EXPERIENCE_COLUMN_WIDTHS.validity, pr: 5 }}>
          <ChecklistStatusDot hideLabel status={row.validityOk ? 'approved' : 'requires approval'} />
        </TableCell>
        <TableCell sx={{ width: BUNDLED_EXPERIENCE_COLUMN_WIDTHS.blackouts }}>
          <ChecklistStatusDot hideLabel status={row.blackoutsOk ? 'approved' : 'requires approval'} />
        </TableCell>
        <TableCell sx={{ width: BUNDLED_EXPERIENCE_COLUMN_WIDTHS.currency }}>
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <ChecklistStatusDot hideLabel status={row.currencyOk ? 'approved' : 'requires approval'} />
            <Typography variant="body2" sx={{ minWidth: 0, overflowWrap: 'break-word' }}>
              {row.currency}
            </Typography>
          </Stack>
        </TableCell>
        <TableCell sx={{ width: BUNDLED_EXPERIENCE_COLUMN_WIDTHS.childAge }}>
          <Stack direction="row" alignItems="flex-start" spacing={0.75}>
            <Box sx={{ mt: 0.5 }}>
              <ChecklistStatusDot hideLabel status={row.childAgeOk ? 'approved' : 'requires approval'} />
            </Box>
            <Typography variant="body2" sx={{ minWidth: 0, overflowWrap: 'break-word' }}>
              {row.childAge}
            </Typography>
          </Stack>
        </TableCell>
        <TableCell sx={{ width: BUNDLED_EXPERIENCE_COLUMN_WIDTHS.action }} onClick={(event) => event.stopPropagation()}>
          <Stack direction="row" spacing={1} alignItems="center">
            <ApprovalActionButton
              approved={approved}
              onApprove={() => onApprovalChange(row.id)}
              onUnapprove={() => onUnapprovalChange(row.id)}
            />
          </Stack>
        </TableCell>
      </TableRow>
      {dealCount > 0 && (
        <TableRow sx={approved ? CHECKLIST_STATUS_STYLES.approvedRow : undefined}>
          <TableCell colSpan={6} sx={{ p: 0, borderBottom: dealsExpanded ? undefined : 'none' }}>
            <Collapse in={dealsExpanded} timeout="auto" unmountOnExit>
              <Box sx={{ bgcolor: 'grey.50', borderLeft: 4, borderLeftColor: 'divider', px: 2, py: 1.5 }}>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="text.secondary"
                  sx={{ letterSpacing: '0.08em', display: 'block', mb: 1 }}
                >
                  BUNDLED EXPERIENCE
                </Typography>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  {row.bundledExperience}
                </Typography>
                <ValiditySection row={row} />
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="text.secondary"
                  sx={{ letterSpacing: '0.08em', display: 'block', mb: 1 }}
                >
                  LINKED DEAL OPTIONS
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {row.linkedDealOptions.map((dealOption) => (
                    <Chip key={dealOption} label={dealOption} size="small" variant="outlined" />
                  ))}
                </Stack>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
